import { cva } from "class-variance-authority";
import { cn } from "cn";
import {
	CircleAlertIcon,
	CircleCheckIcon,
	InfoIcon,
	TriangleAlertIcon,
	XIcon,
} from "lucide-react";
import { useState } from "react";
import { Button } from "#/components/Button/Button";

const alertVariants = cva(
	"relative w-full rounded-lg border border-solid p-4 text-left",
	{
		variants: {
			severity: {
				info: "",
				success: "",
				warning: "",
				error: "",
			},
			prominent: {
				true: "",
				false: "",
			},
		},
		compoundVariants: [
			{
				prominent: false,
				className: "border-border-default bg-surface-secondary",
			},
			{
				severity: "success",
				prominent: true,
				className: "border-border-success bg-surface-green",
			},
			{
				severity: "warning",
				prominent: true,
				className: "border-border-warning bg-surface-orange",
			},
			{
				severity: "error",
				prominent: true,
				className: "border-border-destructive bg-surface-red",
			},
		],
		defaultVariants: {
			severity: "info",
			prominent: false,
		},
	},
);

const severityIcons = {
	info: { icon: InfoIcon, className: "text-highlight-sky" },
	success: { icon: CircleCheckIcon, className: "text-content-success" },
	warning: { icon: TriangleAlertIcon, className: "text-content-warning" },
	error: { icon: CircleAlertIcon, className: "text-content-destructive" },
} as const;

export type AlertColor = "info" | "success" | "warning" | "error";

export type AlertProps = {
	actions?: React.ReactNode;
	dismissible?: boolean;
	onDismiss?: () => void;
	severity?: AlertColor;
	prominent?: boolean;
	children?: React.ReactNode;
	className?: string;
};

export const Alert: React.FC<AlertProps> = ({
	children,
	actions,
	dismissible,
	severity = "info",
	prominent = false,
	onDismiss,
	className,
	...props
}) => {
	const [open, setOpen] = useState(true);

	if (!open) {
		return null;
	}

	const { icon: Icon, className: iconClassName } = severityIcons[severity];

	return (
		<div
			role="alert"
			className={cn(alertVariants({ severity, prominent }), className)}
			{...props}
		>
			<div
				className={cn(
					"relative flex flex-col justify-between gap-4 text-sm",
					dismissible ? "pr-8" : undefined,
				)}
			>
				<div className="flex min-w-0 flex-1 flex-row items-start gap-3">
					<Icon className={cn("size-icon-sm mt-[3px]", iconClassName)} />
					<div className="flex min-w-0 flex-1 flex-col gap-4">
						<div className="min-w-0 flex-1">{children}</div>
						{actions && (
							<div className="flex items-center gap-2">{actions}</div>
						)}
					</div>
				</div>
			</div>
			{dismissible && (
				<Button
					variant="subtle"
					size="icon"
					className="size-8 min-w-0! p-0! absolute top-3 right-3"
					onClick={() => {
						setOpen(false);
						onDismiss?.();
					}}
					data-testid="dismiss-banner-btn"
					aria-label="Dismiss"
				>
					<XIcon className="p-0! size-icon-lg" />
				</Button>
			)}
		</div>
	);
};

export const AlertDescription: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	return (
		<span className="m-0 text-sm" data-pixel="ignore">
			{children}
		</span>
	);
};

export const AlertTitle: React.FC<React.ComponentProps<"h2">> = ({
	className,
	children,
	...props
}) => {
	return (
		<h2 className={cn("m-0 text-sm", className)} {...props}>
			{children}
		</h2>
	);
};
